import { useState, useEffect, useRef } from 'react';
import { useI18n } from '../lib/i18n';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { GoogleIcon } from '../components/icons/navIcons';
import { CheckIcon, AlertCircleIcon, RefreshIcon } from '../components/icons/uiIcons';
import {
  GoogleStatCards,
  ConnectedAccountsCard,
  ServicesGrid,
  GoogleSheetsManager,
} from '../components/google';
import {
  googleAuthUrl,
  googleAccountsList,
  googleSyncData,
  googleUnlink,
  scopeToServices,
  type GoogleAccountDoc,
  type GoogleAccountsStats,
} from '../lib/google';
import ImportSubscribersModal from '../components/subscribers/ImportSubscribersModal';

export default function GoogleIntegration() {
  const { t } = useI18n();

  const [accounts, setAccounts] = useState<GoogleAccountDoc[]>([]);
  const [stats, setStats] = useState<GoogleAccountsStats>({
    accounts: 0,
    sheetsAccess: 0,
    calendarAccess: 0,
    limitedAccess: 0,
  });
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const mountedRef = useRef(true);

  const loadData = async () => {
    try {
      if (mountedRef.current) setLoading(true);
      const res = await googleAccountsList();
      if (!mountedRef.current) return;
      setAccounts(res.accounts || []);
      setStats(
        res.stats || {
          accounts: 0,
          sheetsAccess: 0,
          calendarAccess: 0,
          limitedAccess: 0,
        },
      );
      if (res.accounts && res.accounts.length > 0) {
        if (!selectedAccountId || !res.accounts.some((a) => a.id === selectedAccountId)) {
          setSelectedAccountId(res.accounts[0].id);
        }
      } else {
        setSelectedAccountId(null);
      }
    } catch (err: any) {
      if (!mountedRef.current) return;
      showToast('error', err.message || 'Failed to load Google accounts');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    // Detect redirect callback queries
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === '1') {
      showToast('success', t('google.toast.connectSuccess') || 'Google account connected successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('error')) {
      showToast('error', t('google.toast.connectError') || 'Google authentication failed or was canceled.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    loadData();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleConnectGoogle = async () => {
    try {
      setConnecting(true);
      const res = await googleAuthUrl();
      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err: any) {
      showToast('error', err.message || 'Failed to initiate Google authorization');
      setConnecting(false);
    }
  };

  const handleSyncAccount = async (accountId: string) => {
    try {
      await googleSyncData(accountId);
      showToast('success', t('google.toast.syncSuccess') || 'Account data synchronized successfully.');
      await loadData();
    } catch (err: any) {
      showToast('error', err.message || 'Sync failed. You may need to reconnect the account.');
      throw err;
    }
  };

  const handleUnlinkAccount = async (accountId: string) => {
    try {
      await googleUnlink(accountId);
      showToast('success', t('google.toast.unlinkSuccess') || 'Google account unlinked.');
      await loadData();
    } catch (err: any) {
      showToast('error', err.message || 'Failed to unlink Google account.');
      throw err;
    }
  };

  // Combine scopes across all connected accounts
  const allScopes = accounts.flatMap((acc) => acc.scopes || []);
  const servicesStatus = scopeToServices(allScopes);

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 end-4 z-50 flex items-center gap-2 rounded-xl px-4 py-3 text-xs font-semibold shadow-lg transition-all animate-in fade-in slide-in-from-top-2 ${
            toast.type === 'success'
              ? 'bg-emerald-600 text-white'
              : 'bg-danger text-white'
          }`}
        >
          {toast.type === 'success' ? <CheckIcon size={16} /> : <AlertCircleIcon size={16} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Page Header */}
      <PageHeader
        title={t('google.title') || 'Google Integration'}
        subtitle={
          t('google.subtitle') ||
          'Connect your Google Workspace or Gmail to automate Sheets, Contacts, and Calendar.'
        }
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={loadData}
              disabled={loading}
              leadingIcon={
                <RefreshIcon size={14} className={loading ? 'animate-spin' : ''} />
              }
            >
              {t('common.refresh') || 'Refresh'}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConnectGoogle}
              loading={connecting}
              leadingIcon={<GoogleIcon size={16} />}
            >
              {t('google.connectBtn') || 'Sign in with Google'}
            </Button>
          </div>
        }
      />

      {/* 4 Stat Cards */}
      <GoogleStatCards stats={stats} loading={loading} />

      {/* Connected Accounts Manager */}
      <ConnectedAccountsCard
        accounts={accounts}
        loading={loading}
        onConnectGoogle={handleConnectGoogle}
        onSyncAccount={handleSyncAccount}
        onUnlinkAccount={handleUnlinkAccount}
      />

      {/* Services Grid (Sheets, Drive, Calendar, Contacts) */}
      <ServicesGrid
        services={servicesStatus}
        hasAccounts={accounts.length > 0}
        onImportContacts={() => setIsImportModalOpen(true)}
      />

      {/* Google Sheets Manager (Tracked Sheets, Create New Sheet, Add Existing) */}
      <GoogleSheetsManager
        accounts={accounts}
        selectedAccountId={selectedAccountId}
        onSelectAccount={setSelectedAccountId}
      />

      {/* Subscribers Import Modal with Contacts tab */}
      {isImportModalOpen && (
        <ImportSubscribersModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          initialTab="contacts"
          onSuccess={() => {
            showToast('success', t('google.toast.contactsImportSuccess') || 'Google contacts imported successfully!');
          }}
        />
      )}
    </div>
  );
}
